// API сервис для интеграции с backend
import authService from './auth';

// Пустая база: same-origin (/api/v1/...). В dev — proxy в package.json → бэкенд; в Docker — nginx → coffee-api.
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Make authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  // Ensure token is valid before making request
  await authService.ensureValidToken();

  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    ...authService.getAuthHeader(),
    ...options.headers
  };

  const response = await fetch(url, {
    ...options,
    headers
  });

  const buildErrorMessage = (error, status) => {
    if (!error) {
      return `HTTP ${status}`;
    }

    if (typeof error.detail === 'string') {
      return error.detail;
    }

    if (typeof error.detail === 'object' && error.detail !== null) {
      if (typeof error.detail.message === 'string') {
        return error.detail.message;
      }
      try {
        return JSON.stringify(error.detail);
      } catch {
        // ignore
      }
    }

    try {
      return JSON.stringify(error);
    } catch {
      return `HTTP ${status}`;
    }
  };

  if (!response.ok) {
    if (response.status === 401) {
      // Token expired or invalid, try to refresh once
      const refreshed = await authService.refreshToken();
      if (refreshed) {
        // Retry the request with new token
        const retryHeaders = {
          ...authService.getAuthHeader(),
          ...options.headers
        };
        const retryResponse = await fetch(url, {
          ...options,
          headers: retryHeaders
        });
        if (!retryResponse.ok) {
          if (retryResponse.status === 401) {
            authService.clearAuth();
            throw new Error('Session expired. Please authenticate again.');
          }
          const error = await retryResponse.json().catch(() => ({ detail: 'Request failed' }));
          throw new Error(buildErrorMessage(error, retryResponse.status));
        }
        return retryResponse.json();
      } else {
        authService.clearAuth();
        throw new Error('Session expired. Please authenticate again.');
      }
    }
    
    // Специальная обработка ошибки 409 Conflict
    if (response.status === 409) {
      const error = await response.json().catch(() => ({ detail: 'Conflict' }));
      const errorMessage = buildErrorMessage(error, response.status);
      const conflictError = new Error(errorMessage);
      conflictError.status = 409;
      conflictError.isConflict = true;
      throw conflictError;
    }
    
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(buildErrorMessage(error, response.status));
  }

  return response.json();
}

// API методы
export const apiService = {
  // Получение списка кофеен
  async getCoffeeShops() {
    return apiRequest('/api/v1/coffee-shops', {
      method: 'GET'
    });
  },

  // Получение информации о кофейне
  async getCoffeeShop(id) {
    return apiRequest(`/api/v1/coffee-shops/${id}`, {
      method: 'GET'
    });
  },

  // Поиск кофеен по запросу
  async searchCoffeeShops(query, limit = 10) {
    const params = new URLSearchParams();
    if (query) params.append('query', query);
    if (limit) params.append('limit', limit);
    
    return apiRequest(`/api/v1/coffee-shops/search?${params.toString()}`, {
      method: 'GET'
    });
  },

  // Получение меню кофейни (сортировка по id от меньшего к большему)
  async getMenuItems(coffeeShopId) {
    try {
      const params = new URLSearchParams();
      if (coffeeShopId != null) params.append('coffee_shop_id', coffeeShopId);
      params.append('sort_by', 'id');
      params.append('sort_order', 'asc');
      const data = await apiRequest(`/api/v1/menu-items?${params.toString()}`, {
        method: 'GET'
      });

      // Адаптируем структуру данных под существующий интерфейс
      return Array.isArray(data)
        ? data.map((item) => ({
            id: item.id,
            name: item.name,
            description: `${item.volume_weight || 'Стандартная порция'} • ${item.type}`,
            price: item.price,
            image: item.photo || 'https://via.placeholder.com/200x150?text=Menu+Item',
            category: item.type,
            volume_weight: item.volume_weight,
            type: item.type,
            coffee_shop_id: item.coffee_shop_id,
            sizes: item.sizes || [],
            addons: item.addons || [],
          }))
        : [];
    } catch (error) {
      console.error('Error fetching menu items:', error);
      throw new Error(`Не удалось загрузить меню: ${error.message}`);
    }
  },

  // Получение уникальных категорий из меню
  getCategoriesFromMenuItems(menuItems) {
    const uniqueTypes = [...new Set(menuItems.map(item => item.type))];
    
    // Маппинг типов на названия (ключи — значения type из API: RU)
    const typeMapping = {
      'Кофе': { name: 'Кофе' },
      'Какао': { name: 'Какао' },
      'Чай': { name: 'Чай' },
      'Матча': { name: 'Матча' },
      'Сок': { name: 'Сок' },
      'Лимонад': { name: 'Лимонад' },
      'Горячий шоколад': { name: 'Горячий шоколад' },
      'Холодные напитки': { name: 'Холодные напитки' },
      'еда': { name: 'Еда' },
      'десерты': { name: 'Десерты' },
      'снэки': { name: 'Снэки' }
    };

    return uniqueTypes.map(type => ({
      id: type,
      name: typeMapping[type]?.name || type.charAt(0).toUpperCase() + type.slice(1)
    }));
  },

  // Создание заказа
  async createOrder(orderData) {
    return apiRequest('/api/v1/orders', {
      method: 'POST',
      body: JSON.stringify(orderData)
    });
  },

  // Получение заказов текущего пользователя через новый endpoint
  async getMyOrders() {
    let currentUser = authService.getUser && authService.getUser();
    
    // Извлекаем ID из возможных структур: user.user.id, user.id, user.user_id
    const extractUserId = (user) => {
      if (!user) return null;
      return user.user?.id || user.user?.user_id || user.id || user.user_id;
    };
    
    // Если пользователь не загружен, загружаем его
    let userId = extractUserId(currentUser);
    if (!currentUser || !userId) {
      try {
        currentUser = await authService.fetchUserInfo();
        userId = extractUserId(currentUser);
      } catch (error) {
        console.error('[API] Failed to fetch user info:', error);
        throw new Error('Не удалось загрузить информацию о пользователе. Попробуйте перезапустить приложение.');
      }
    }
    
    if (!currentUser || !userId) {
      throw new Error('Пользователь не найден. Попробуйте перезапустить приложение.');
    }

    return apiRequest(`/api/v1/orders/users/${userId}`, {
      method: 'GET'
    });
  },

  // Получение заказов пользователя по id (для совместимости)
  async getUserOrders(userId) {
    if (userId) {
      return apiRequest(`/api/v1/orders/users/${userId}`, {
        method: 'GET'
      });
    }
    
    // Извлекаем ID из возможных структур: user.user.id, user.id, user.user_id
    const extractUserId = (user) => {
      if (!user) return null;
      return user.user?.id || user.user?.user_id || user.id || user.user_id;
    };
    
    // если id не передан, используем текущего пользователя
    let currentUser = authService.getUser && authService.getUser();
    
    // Если пользователь не загружен, загружаем его
    let finalUserId = extractUserId(currentUser);
    if (!currentUser || !finalUserId) {
      try {
        currentUser = await authService.fetchUserInfo();
        finalUserId = extractUserId(currentUser);
      } catch (error) {
        console.error('[API] Failed to fetch user info:', error);
        throw new Error('Не удалось загрузить информацию о пользователе. Попробуйте перезапустить приложение.');
      }
    }
    
    if (!currentUser || !finalUserId) {
      throw new Error('Пользователь не найден. Попробуйте перезапустить приложение.');
    }

    return apiRequest(`/api/v1/orders/users/${finalUserId}`, {
      method: 'GET'
    });
  },

  // Получение информации о заказе
  async getOrder(orderId) {
    return apiRequest(`/api/v1/orders/${orderId}`, {
      method: 'GET'
    });
  },

  // Обновление статуса заказа
  async updateOrderStatus(orderId, status) {
    return apiRequest(`/api/v1/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  },

  // Отмена заказа
  async cancelOrder(orderId) {
    // Отмена = смена статуса через тот же endpoint
    return this.updateOrderStatus(orderId, 'cancelled');
  },

  /**
   * Промокод, доступный для текущего пользователя (действительный и не использованный).
   * Для баннера/уведомления. Возвращает { valid, code?, discount_amount? } или при ошибке/401 — не бросает, возвращает { valid: false }.
   */
  async getAvailablePromoCode(coffeeShopId = null) {
    const params = new URLSearchParams();
    if (coffeeShopId != null) params.append('coffee_shop_id', coffeeShopId);
    const query = params.toString();
    const url = `/api/v1/promo-codes/available${query ? `?${query}` : ''}`;
    try {
      return await apiRequest(url, { method: 'GET' });
    } catch (e) {
      return { valid: false };
    }
  },

  /**
   * Валидация промокода перед применением в корзине.
   * GET /api/v1/promo-codes/validate/{code}?coffee_shop_id=&order_amount=
   * Возвращает { valid, code?, discount_amount?, reason? }.
   */
  async validatePromoCode(code, coffeeShopId = null, orderAmount = null) {
    const normalized = String(code).trim().toUpperCase();
    if (!normalized) return { valid: false, reason: 'not_found' };
    const params = new URLSearchParams();
    if (coffeeShopId != null) params.append('coffee_shop_id', coffeeShopId);
    if (orderAmount != null) params.append('order_amount', String(orderAmount));
    const query = params.toString();
    const url = `/api/v1/promo-codes/validate/${encodeURIComponent(normalized)}${query ? `?${query}` : ''}`;
    return apiRequest(url, { method: 'GET' });
  },

  async getLoyaltyMemberships(coffeeShopId = null) {
    const params = new URLSearchParams();
    if (coffeeShopId != null) params.append('coffee_shop_id', String(coffeeShopId));
    const query = params.toString();
    try {
      return await apiRequest(`/api/v1/loyalty/me${query ? `?${query}` : ''}`, {
        method: 'GET',
      });
    } catch (e) {
      return [];
    }
  },

  async previewLoyalty({ coffeeShopId, subtotal, promoDiscount = 0, bonusSpend = 0 }) {
    return apiRequest('/api/v1/loyalty/preview', {
      method: 'POST',
      body: JSON.stringify({
        coffee_shop_id: coffeeShopId,
        subtotal,
        promo_discount: promoDiscount,
        bonus_spend: bonusSpend,
      }),
    });
  },

  async updateLoyaltyMembership(programId, data) {
    return apiRequest(`/api/v1/loyalty/memberships/${programId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async claimLoyalty(phone, programId = null) {
    const body = { phone: String(phone).trim() };
    if (programId != null) body.program_id = programId;
    return apiRequest('/api/v1/loyalty/claim', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async submitSupportRequest(payload) {
    return apiRequest('/api/v1/support/requests', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Редактирование заказа
  async updateOrder(orderId, orderData) {
    // Эндпоинт для обновления позиций заказа: PUT /api/v1/orders/{order_id}/items
    // Формат данных: { items: [...], reason?: string }
    return apiRequest(`/api/v1/orders/${orderId}/items`, {
      method: 'PUT',
      body: JSON.stringify(orderData)
    });
  }
};

// Утилиты для работы с API
export const apiUtils = {
  // Обработка ошибок API
  handleApiError(error) {
    console.error('API Error:', error);
    return {
      message: error.message || 'Произошла ошибка при обращении к серверу',
      code: error.code || 'UNKNOWN_ERROR'
    };
  },

  // Форматирование данных заказа для отправки
  formatOrderData(cartItems, selectedCafe, userInfo) {
    return {
      items: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        size: item.size,
        customizations: item.customizations
      })),
      cafeId: selectedCafe.id,
      totalPrice: cartItems.reduce((total, item) => total + (item.price * item.quantity), 0),
      userInfo,
      createdAt: new Date().toISOString()
    };
  }
};

export default apiService;
